import React, { useState } from 'react';
import { updateDossier } from '../../lib/api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { CheckCircle2, Eye, Phone, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

/**
 * Affiché quand statut == "devis_envoye". Permet de cocher :
 * - devis consulté (manuel ou via tracking email futur)
 * - appel post-devis effectué (obligatoire pour particulier/pro, info-only pour syndic)
 */
export const PostDevisChecklist = ({ dossier, prospect, onUpdated }) => {
    const [callNotes, setCallNotes] = useState(dossier.appel_post_devis_notes || '');
    const [showNotes, setShowNotes] = useState(false);
    const [saving, setSaving] = useState(false);

    const isMandatoryCall = prospect && (prospect.type_contact === 'particulier' || prospect.type_contact === 'professionnel');
    const callDone = !!dossier.appel_post_devis_effectue;
    const consulte = !!dossier.devis_consulte;

    const update = async (patch) => {
        setSaving(true);
        try {
            await updateDossier(dossier.id, patch);
            onUpdated && onUpdated();
        } catch {
            toast.error('Erreur de sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const toggleConsulte = () => {
        update({
            devis_consulte: !consulte,
            date_devis_consulte: !consulte ? new Date().toISOString() : null
        });
    };

    const toggleAppel = () => {
        update({
            appel_post_devis_effectue: !callDone,
            date_appel_post_devis: !callDone ? new Date().toISOString() : null,
            appel_post_devis_notes: !callDone ? callNotes : dossier.appel_post_devis_notes
        });
    };

    const saveNotes = async () => {
        await update({ appel_post_devis_notes: callNotes });
        toast.success('Notes enregistrées');
        setShowNotes(false);
    };

    return (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-sm space-y-3" data-testid="post-devis-checklist">
            <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-amber-900">Suivi post-envoi du devis</h3>
            </div>

            <div className="space-y-2">
                <button
                    onClick={toggleConsulte}
                    disabled={saving}
                    className={`w-full flex items-center justify-between gap-3 p-3 rounded-sm border transition-all text-left ${
                        consulte ? 'bg-green-50 border-green-300' : 'bg-white border-[#E4E4E7] hover:border-amber-400'
                    }`}
                    data-testid="toggle-devis-consulte"
                >
                    <div className="flex items-center gap-3">
                        {consulte ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                            <Eye className="w-5 h-5 text-amber-600" />
                        )}
                        <div>
                            <p className="text-sm font-semibold text-[#0A0A0A]">
                                Devis consulté par le client
                            </p>
                            {consulte && dossier.date_devis_consulte && (
                                <p className="text-xs text-green-700">
                                    Le {format(new Date(dossier.date_devis_consulte), 'dd MMM yyyy à HH:mm', { locale: fr })}
                                </p>
                            )}
                        </div>
                    </div>
                </button>

                <button
                    onClick={toggleAppel}
                    disabled={saving}
                    className={`w-full flex items-center justify-between gap-3 p-3 rounded-sm border transition-all text-left ${
                        callDone
                            ? 'bg-green-50 border-green-300'
                            : isMandatoryCall
                                ? 'bg-red-50 border-red-300 hover:bg-red-100'
                                : 'bg-white border-[#E4E4E7] hover:border-amber-400'
                    }`}
                    data-testid="toggle-appel-post-devis"
                >
                    <div className="flex items-center gap-3">
                        {callDone ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                            <Phone className={`w-5 h-5 ${isMandatoryCall ? 'text-red-600' : 'text-amber-600'}`} />
                        )}
                        <div>
                            <p className="text-sm font-semibold text-[#0A0A0A]">
                                Appel post-devis effectué
                                {isMandatoryCall && !callDone && (
                                    <span className="ml-2 text-xs text-red-600 font-bold">OBLIGATOIRE</span>
                                )}
                                {!isMandatoryCall && (
                                    <span className="ml-2 text-xs text-[#52525B]">(non requis pour syndic/SCI)</span>
                                )}
                            </p>
                            {callDone && dossier.date_appel_post_devis && (
                                <p className="text-xs text-green-700">
                                    Le {format(new Date(dossier.date_appel_post_devis), 'dd MMM yyyy à HH:mm', { locale: fr })}
                                </p>
                            )}
                        </div>
                    </div>
                </button>

                {callDone && (
                    <div className="pl-3">
                        {!showNotes && (
                            <Button
                                variant="link"
                                onClick={() => setShowNotes(true)}
                                className="text-amber-700 h-auto p-0 text-xs"
                            >
                                {dossier.appel_post_devis_notes ? 'Voir/modifier les notes' : '+ Ajouter notes de l\'appel'}
                            </Button>
                        )}
                        {showNotes && (
                            <div className="space-y-2 mt-2">
                                <Textarea
                                    value={callNotes}
                                    onChange={(e) => setCallNotes(e.target.value)}
                                    rows={3}
                                    className="rounded-sm bg-white"
                                    placeholder="Retours du client, prochaines étapes..."
                                    data-testid="appel-notes-input"
                                />
                                <div className="flex gap-2">
                                    <Button
                                        onClick={saveNotes}
                                        size="sm"
                                        className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
                                        data-testid="save-appel-notes"
                                    >
                                        Enregistrer
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => { setCallNotes(dossier.appel_post_devis_notes || ''); setShowNotes(false); }}
                                        className="rounded-sm"
                                    >
                                        Annuler
                                    </Button>
                                </div>
                            </div>
                        )}
                        {!showNotes && dossier.appel_post_devis_notes && (
                            <p className="text-xs text-[#52525B] mt-1 italic whitespace-pre-wrap">
                                "{dossier.appel_post_devis_notes}"
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
